import React, { useEffect, useState, useMemo, useRef } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import { FaUpload, FaFilePdf, FaFileImage, FaTimes } from "react-icons/fa"
import { fetchData } from "utils/Common_api_functions"

const UploadDocumentModal = ({ open, toggle, customer, onUpload, prefill }) => {
  const [form, setForm] = useState({ categoryId: "", documentId: "", files: [], year: "" })
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const fileInputRef = useRef(null)
  // const [allDocumentTypes, setAllDocumentTypes] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(false)
  const [uploadFor, setUploadFor] = useState("")

  useEffect(() => {
    if (open) {
      getCategories()
    }
  }, [open])

  useEffect(() => {
    if (open && prefill) {
      setUploadFor(String(prefill.uploadFor))
      setForm(prev => ({
        ...prev,
        categoryId: String(prefill.categoryId),
        documentId: String(prefill.documentId),
        year: prefill.year || "",
      }))
    }
  }, [open])

  async function getCategories() {
    const APIdata = await fetchData(setLoadingTypes, "document-category")
    if (APIdata?.success) {
      setCategories(APIdata?.data || [])
    }
  }

  // async function getDocumentTypes() {
  //   const APIdata = await fetchData(setLoadingTypes, "document-types")
  //   if (APIdata?.success) {
  //     setAllDocumentTypes(APIdata?.data || [])
  //   }
  // }

  const filteredDocumentTypes = useMemo(() => {
    if (!form.categoryId) return []

    const selectedCategory = categories.find(
      cat => String(cat.id) === String(form.categoryId)
    )

    // return selectedCategory?.documentTypes || []

    return (
      selectedCategory?.documentTypes?.filter(
        type => type.status === true
      ) || []
    )
  }, [form.categoryId, categories])

  const handleChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value }
      if (field === "categoryId") updated.documentId = ""
      return updated
    })
    setErrors(prev => ({ ...prev, [field]: "" }))
  }

  const handleFileChange = e => {
    const selected = Array.from(e.target.files)
    if (!selected.length) return

    const fileErrors = []
    const validFiles = []

    selected.forEach(file => {
      if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
        fileErrors.push(`"${file.name}" is not a PDF or image`)
      } else {
        validFiles.push(file)
      }
    })

    if (fileErrors.length) {
      setErrors(prev => ({ ...prev, file: fileErrors.join("; ") }))
    } else {
      setErrors(prev => ({ ...prev, file: "" }))
    }

    if (validFiles.length) {
      setForm(prev => ({ ...prev, files: [...prev.files, ...validFiles] }))
    }

    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const removeFile = index => {
    setForm(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))
  }

  const validate = () => {
    const errs = {}
    if (!uploadFor) errs.uploadFor = "Please select person"
    if (!form.categoryId) errs.categoryId = "Category is required"
    if (!form.documentId) errs.documentId = "Document type is required"
    // if (!form.year) errs.year = "Financial year is required"
    if (!form.files.length) errs.file = "Please upload at least one file"
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const resetForm = () => {
    setForm({ categoryId: "", documentId: "", files: [], year: "" })
    setErrors({})
    setUploadFor("")
  }

  const handleSubmit = () => {
    if (!validate()) return
    const isCustomer = String(uploadFor) === String(customer?.id)
    onUpload({
      documentId: form.documentId,
      files: form.files,
      year: form.year,
      customerId: customer?.id,
      uploadedFor: uploadFor,
      isCustomer,
    })
    resetForm()
    toggle()
  }

  const handleClose = () => {
    resetForm()
    toggle()
  }

  const renderError = field =>
    errors[field] && (
      <div className="invalid-feedback d-block">{errors[field]}</div>
    )

  const uploadOptions = [
    { label: customer?.customerName, value: customer?.id },
    ...(customer?.members || [])
      .filter(m => m.status === true)
      .map(m => ({ label: m.name, value: m.id })),
  ]

  useEffect(() => {
    if (open && uploadOptions.length === 1) {
      setUploadFor(String(uploadOptions[0].value))
    }
  }, [open, uploadOptions])

  const generateFinancialYears = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = -10; i <= 0; i++) {
      const fyStart = currentYear + i
      const fyEnd = fyStart + 1
      const ayStart = fyEnd
      const ayEnd = ayStart + 1
      const formatted = `AY-${ayStart}/${String(ayEnd).slice(-2)} (FY-${fyStart}/${String(fyEnd).slice(-2)})`
      years.push({ label: formatted, value: formatted })
    }
    return years.reverse()
  }

  const financialYears = generateFinancialYears()

  return (
    <Modal isOpen={open} centered size="md" toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        Upload Document
        {/* {customer && (
          <small className="d-block text-muted fw-normal" style={{ fontSize: 12 }}>
            for {customer.customerName}
          </small>
        )} */}

        <div>
        <Button className="modal-btn upload-btn" onClick={handleSubmit}>
          <FaUpload className="" /> Upload Document
        </Button>
        </div>
      </ModalHeader>

      <ModalBody>
        <Form>
          <FormGroup>
            <Label>
              Upload For <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={uploadFor}
              onChange={e => {
                setUploadFor(e.target.value)
                setErrors(prev => ({ ...prev, uploadFor: "" }))
              }}
              className={`rounded-4 ${errors.uploadFor ? "is-invalid" : ""}`}
            >
              <option value="">Select person</option>
              {uploadOptions.map((opt, i) => (
                <option key={i} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
            {renderError("uploadFor")}
          </FormGroup>

          <FormGroup>
            <Label>
              Category <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={form.categoryId}
              onChange={e => handleChange("categoryId", e.target.value)}
              className={`rounded-4 ${errors.categoryId ? "is-invalid" : ""}`}
              disabled={loadingTypes}
            >
              <option value="">
                {loadingTypes ? "Loading..." : "Select category"}
              </option>
              {/* {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.documentCategory}
                </option>
              ))} */}
              {categories
                .filter(cat => cat.status === true)
                .map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.documentCategory}
                  </option>
                ))}
            </Input>
            {renderError("categoryId")}
          </FormGroup>

          <FormGroup>
            <Label>
              Type of Document <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={form.documentId}
              onChange={e => handleChange("documentId", e.target.value)}
              className={`rounded-4 ${errors.documentId ? "is-invalid" : ""}`}
              disabled={!form.categoryId || loadingTypes}
            >
              <option value="">
                {!form.categoryId
                  ? "Select a category first"
                  : filteredDocumentTypes.length === 0
                    ? "No document types for this category"
                    : "Select document type"}
              </option>
              {filteredDocumentTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.documentType}
                </option>
              ))}
            </Input>
            {renderError("documentId")}
          </FormGroup>

          <FormGroup>
            <Label>
              Financial Year
            </Label>
            <Input
              type="select"
              value={form.year}
              onChange={e => handleChange("year", e.target.value)}
              className={`rounded-4 ${errors.year ? "is-invalid" : ""}`}
            >
              <option value="">Select financial year</option>
              {financialYears.map((fy, i) => (
                <option key={i} value={fy.value}>
                  {fy.label}
                </option>
              ))}
            </Input>
            {renderError("year")}
          </FormGroup>

          <FormGroup>
            <Label>
              Upload Files <span className="text-danger">*</span>
            </Label>
            <label
              style={{
                display: "block",
                border: `2px dashed ${errors.file ? "#dc3545" : "rgba(85,110,230,0.3)"}`,
                borderRadius: 10,
                padding: "20px",
                textAlign: "center",
                cursor: "pointer",
                background: form.files.length ? "rgba(85,110,230,0.04)" : "transparent",
                transition: "all 0.2s",
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf,image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <FaUpload size={24} color="#556ee6" />
              <p className="mb-0 mt-1" style={{ fontSize: 13, color: "#666" }}>
                {form.files.length ? "Click to add more files" : "Click to upload file(s)"}
              </p>
              <p className="mb-0" style={{ fontSize: 11, color: "#999" }}>
                PDF or Image files — multiple allowed
              </p>
            </label>

            {form.files.length > 0 && (
              <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {form.files.map((file, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#f8f9fa",
                      borderRadius: 8,
                      padding: "6px 10px",
                    }}
                  >
                    {file.type.startsWith("image/")
                      ? <FaFileImage color="#556ee6" size={16} />
                      : <FaFilePdf color="#dc3545" size={16} />
                    }
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#333", wordBreak: "break-all" }}>
                      {file.name}
                    </span>
                    <span style={{ fontSize: 11, color: "#999", whiteSpace: "nowrap" }}>
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <FaTimes
                      size={13}
                      color="#dc3545"
                      style={{ cursor: "pointer", flexShrink: 0 }}
                      onClick={e => { e.preventDefault(); removeFile(i) }}
                    />
                  </div>
                ))}
              </div>
            )}

            {renderError("file")}
          </FormGroup>
        </Form>
      </ModalBody>

      {/* <ModalFooter>
        <Button className="btn-blue" onClick={handleSubmit}>
          <FaUpload className="me-2" /> Upload Document
        </Button>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </ModalFooter> */}
    </Modal>
  )
}

export default UploadDocumentModal
